import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";

// La fuente de verdad de la expiración es el exp del JWT (cabecera X-Session-Exp,
// guardada en localStorage por el interceptor de api.js). El backend SOLO renueva
// la sesión en POST /auth/session/renew, y este componente SOLO lo llama ante
// actividad real del usuario (eventos de interacción) o desde el botón del aviso.
// Ninguna petición automática (VersionGuard, cargas en background) renueva.
const WARN_SECONDS = 5 * 60; // aviso 5 min antes de la caducidad
const RENEW_UNDER_SECONDS = 30 * 60; // actividad real con menos de 30 min restantes renueva
const ACTIVITY_THROTTLE_MS = 60 * 1000;

function readExp() {
  const v = Number(localStorage.getItem("ap_session_exp") || 0);
  return Number.isFinite(v) ? v : 0;
}

export default function SessionGuard() {
  const [warn, setWarn] = useState(false);

  const renew = useCallback(async () => {
    try {
      await api.post("/auth/session/renew");
      setWarn(false);
    } catch {
      // si ya no es renovable (12 h absolutas), el interceptor 401 llevará al login
    }
  }, []);

  useEffect(() => {
    let lastActivity = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - lastActivity < ACTIVITY_THROTTLE_MS) return;
      lastActivity = now;
      const remaining = readExp() - Math.floor(now / 1000);
      if (remaining > 0 && remaining < RENEW_UNDER_SECONDS) renew();
    };
    const events = ["pointerdown", "keydown", "touchstart", "wheel"];
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));

    const tick = () => {
      const remaining = readExp() - Math.floor(Date.now() / 1000);
      setWarn(remaining > 0 && remaining <= WARN_SECONDS);
    };
    const interval = setInterval(tick, 15000);
    window.addEventListener("session:exp", tick);
    window.addEventListener("storage", tick);
    tick();
    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      clearInterval(interval);
      window.removeEventListener("session:exp", tick);
      window.removeEventListener("storage", tick);
    };
  }, [renew]);

  if (!warn) return null;
  return (
    <div
      data-testid="session-expiry-banner"
      className="fixed top-4 inset-x-4 sm:inset-x-auto sm:right-6 z-[220] sm:max-w-sm bg-zinc-900 text-white rounded-2xl shadow-lg px-5 py-4 flex flex-col gap-3"
    >
      <p className="text-sm leading-snug">Tu sesión está a punto de caducar por inactividad.</p>
      <button
        onClick={renew}
        data-testid="session-continue-button"
        className="self-start h-10 px-5 rounded-full bg-white text-zinc-900 text-sm font-medium hover:bg-zinc-100 active:scale-[0.98] transition-[background-color,transform] duration-200"
      >
        Continuar sesión
      </button>
    </div>
  );
}
